"use client";

import { useCallback, useEffect, useRef } from "react";
import { queryClient } from "@/config/react-query";
import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";
import type { AlertDetail, NoteTemplateUpdatedData } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import type { NoteDetail, NoteTemplateTableItem } from "@/types/notes";
import { noteQueryKeys } from "@/api/provider/scribe/note-query";
import { invalidateNotesUsingTemplate, upsertNoteTemplateList } from "@/api/provider/scribe/note-template-cache";
const MAX_TRACKED_TEMPLATE_EVENTS = 1000;
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;
const isValidDate = (value: unknown) => typeof value === "string" && Number.isFinite(Date.parse(value));
const hasValidQuestionData = (question: Record<string, unknown>) => {
  if (!isRecord(question.data)) return false;
  const placeholders = question.data.placeholders;
  return placeholders === undefined || Array.isArray(placeholders) && placeholders.every(placeholder => isRecord(placeholder) && typeof placeholder.id === "string");
};
const isNoteTemplateUpdatedData = (value: unknown): value is NoteTemplateUpdatedData => {
  if (!isRecord(value)) return false;
  const {
    template,
    isArchived,
    mutationRevision
  } = value;
  if (typeof isArchived !== "boolean" || !Number.isSafeInteger(mutationRevision) || Number(mutationRevision) < 1) {
    return false;
  }
  if (!isRecord(template)) return false;
  const {
    id,
    name,
    questions,
    sections,
    createdDate,
    mutationRevision: templateMutationRevision
  } = template;
  return typeof id === "string" && id.length > 0 && typeof name === "string" && templateMutationRevision === mutationRevision && Array.isArray(questions) && questions.every(question => isRecord(question) && typeof question.id === "string" && typeof question.questionType === "string" && hasValidQuestionData(question)) && Array.isArray(sections) && sections.every(section => isRecord(section) && typeof section.id === "string") && isValidDate(createdDate);
};
const eventVersion = (payload: NoteTemplateUpdatedData) => payload.mutationRevision;
const templateVersion = (template: NoteTemplateTableItem) => template.mutationRevision ?? 0;
const findCurrentQuestion = (current: NoteTemplateTableItem, incomingQuestion: NoteTemplateTableItem["questions"][number]) => current.questions.find(candidate => candidate.id === incomingQuestion.id) ?? current.questions.find(candidate => candidate.questionType === incomingQuestion.questionType && incomingQuestion.questionType === "SCRIBE_ATTACHMENTS");
const mergePlaceholderSignedUrls = (current: NoteTemplateTableItem, incoming: Omit<NoteTemplateTableItem, "isFavorited">): NoteTemplateTableItem => {
  const questions = incoming.questions.map(question => {
    // Structural edits archive/recreate questions, so their IDs change. There
    // can be only one SCRIBE_ATTACHMENTS question per template; use that as a
    // safe fallback to preserve its still-valid signed placeholder URLs.
    const currentQuestion = findCurrentQuestion(current, question);
    const incomingData = question.data as {
      placeholders?: Array<Record<string, unknown>>;
    };
    const currentData = currentQuestion?.data as {
      placeholders?: Array<Record<string, unknown>>;
    };
    if (!incomingData.placeholders || !currentData?.placeholders) {
      return question;
    }
    const currentPlaceholders = new Map(currentData.placeholders.map(placeholder => [placeholder.id, placeholder]));
    return {
      ...question,
      data: {
        ...question.data,
        placeholders: incomingData.placeholders.map(placeholder => {
          const currentPlaceholder = currentPlaceholders.get(placeholder.id);
          return currentPlaceholder?.signedUrl && !placeholder.signedUrl && currentPlaceholder.gcsPath === placeholder.gcsPath ? {
            ...placeholder,
            signedUrl: currentPlaceholder.signedUrl
          } : placeholder;
        })
      }
    };
  });
  return {
    ...current,
    ...incoming,
    questions,
    isFavorited: current.isFavorited ?? false
  };
};
const applyEventToList = (cached: NoteTemplateTableItem[] | undefined, payload: NoteTemplateUpdatedData) => {
  if (!cached) return cached;
  const {
    template,
    isArchived
  } = payload;
  const existing = cached.find(item => item.id === template.id);
  if (existing && templateVersion(existing) > eventVersion(payload)) {
    return cached;
  }
  if (isArchived) {
    return existing ? cached.filter(item => item.id !== template.id) : cached;
  }
  return upsertNoteTemplateList(cached, template) ?? cached;
};

/**
 * Drop every cached template entry nothing is currently observing, so the next
 * observer refetches. Costs no network.
 */
function evictInactiveTemplates() {
  queryClient.removeQueries({
    queryKey: noteQueryKeys.templates(),
    exact: true,
    type: "inactive"
  });
  queryClient.removeQueries({
    queryKey: noteQueryKeys.templateDetails(),
    exact: false,
    type: "inactive"
  });
}

/**
 * Keeps list and mounted detail caches synchronized with template broadcasts.
 * Accepted events are journaled so a stale in-flight fetch cannot overwrite a
 * newer frame, while persisted revisions reject out-of-order delivery.
 */
export function useNoteTemplateRealtimeCache() {
  const latestVersionRef = useRef<Map<string, number>>(new Map());
  const archivedVersionRef = useRef<Map<string, number>>(new Map());
  const latestEventRef = useRef<Map<string, NoteTemplateUpdatedData>>(new Map());
  const replayingRef = useRef(false);
  const previousConnectedRef = useRef<boolean | null>(null);
  const detailRefreshTimersRef = useRef<Map<string, Array<ReturnType<typeof setTimeout>>>>(new Map());
  const scheduleDetailRefresh = useCallback((templateId: string) => {
    detailRefreshTimersRef.current.get(templateId)?.forEach(timer => clearTimeout(timer));
    const refetch = () => {
      void queryClient.refetchQueries({
        queryKey: noteQueryKeys.templateDetail(templateId),
        exact: true,
        type: "active"
      });
      void invalidateNotesUsingTemplate(queryClient, templateId);
    };
    detailRefreshTimersRef.current.set(templateId, [1500, 10_000].map(delay => setTimeout(refetch, delay)));
  }, []);
  useEffect(() => () => {
    detailRefreshTimersRef.current.forEach(timers => timers.forEach(clearTimeout));
    detailRefreshTimersRef.current.clear();
  }, []);
  const reconcileSnapshot = useCallback((queryKey: readonly unknown[]) => {
    if (replayingRef.current) return;
    const templatesKey = noteQueryKeys.templates();
    const isList = queryKey.length === templatesKey.length && queryKey.every((part, index) => part === templatesKey[index]);
    const isDetail = queryKey.length === 4 && queryKey[0] === "scribe" && queryKey[1] === "notes" && queryKey[2] === "template-detail";
    if (!isList && !isDetail) return;
    replayingRef.current = true;
    try {
      if (isList) {
        queryClient.setQueryData<NoteTemplateTableItem[]>(templatesKey, cached => {
          if (!cached) return cached;
          let reconciled = cached;
          for (const row of cached) {
            const version = templateVersion(row);
            const journaled = latestEventRef.current.get(row.id);
            if (journaled && version > eventVersion(journaled)) {
              latestEventRef.current.delete(row.id);
              latestVersionRef.current.delete(row.id);
              archivedVersionRef.current.delete(row.id);
            }
          }
          for (const event of Array.from(latestEventRef.current.values())) {
            reconciled = applyEventToList(reconciled, event) ?? reconciled;
          }
          return reconciled;
        });
      } else {
        const templateId = String(queryKey[3]);
        const journaled = latestEventRef.current.get(templateId);
        if (!journaled || journaled.isArchived) return;
        queryClient.setQueryData<NoteTemplateTableItem>(queryKey, cached => {
          if (!cached || templateVersion(cached) > eventVersion(journaled)) {
            return cached;
          }
          return mergePlaceholderSignedUrls(cached, journaled.template);
        });
      }
    } finally {
      replayingRef.current = false;
    }
  }, []);
  useEffect(() => queryClient.getQueryCache().subscribe(event => {
    if (event.type === "updated") {
      reconcileSnapshot(event.query.queryKey);
    }
  }), [reconcileSnapshot]);
  const handleMessage = useCallback((message: AlertDetail) => {
    if (message.type !== AlertType.NOTE_TEMPLATE_UPDATED) return;
    const payload = message.data;
    if (!isNoteTemplateUpdatedData(payload)) return;
    const id = payload.template.id;
    const version = eventVersion(payload);
    const latestVersion = latestVersionRef.current.get(id) ?? -Infinity;
    const archivedVersion = archivedVersionRef.current.get(id);
    const previouslyAccepted = latestEventRef.current.get(id);
    if (version < latestVersion || version === latestVersion && previouslyAccepted !== undefined && (!payload.isArchived || archivedVersion === version)) {
      return;
    }
    latestVersionRef.current.set(id, version);
    if (payload.isArchived) archivedVersionRef.current.set(id, version);
    // Refresh insertion order so this map doubles as a bounded LRU journal.
    latestEventRef.current.delete(id);
    latestEventRef.current.set(id, payload);
    while (latestEventRef.current.size > MAX_TRACKED_TEMPLATE_EVENTS) {
      const oldestId = latestEventRef.current.keys().next().value;
      if (oldestId === undefined) break;
      latestEventRef.current.delete(oldestId);
      latestVersionRef.current.delete(oldestId);
      archivedVersionRef.current.delete(oldestId);
    }
    const detailKey = noteQueryKeys.templateDetail(id);
    replayingRef.current = true;
    try {
      queryClient.setQueryData<NoteTemplateTableItem[]>(noteQueryKeys.templates(), cached => applyEventToList(cached, payload));
      if (!payload.isArchived) {
        queryClient.setQueryData<NoteTemplateTableItem>(detailKey, cached => cached && templateVersion(cached) <= version ? mergePlaceholderSignedUrls(cached, payload.template) : cached);
      }
    } finally {
      replayingRef.current = false;
    }
    if (!payload.isArchived) scheduleDetailRefresh(id);
  }, [scheduleDetailRefresh]);
  const {
    isConnected
  } = useWebSocketCallbackSubscription([AlertType.NOTE_TEMPLATE_UPDATED], handleMessage, true, "useNoteTemplateRealtimeCache");
  useEffect(() => {
    const timers_0: Array<ReturnType<typeof setTimeout>> = [];
    if (previousConnectedRef.current === false && isConnected) {
      // Pubsub-only events are not replayable. Reconcile after the usual replica
      // window, then retry once for replicas that are still catching up.
      //
      // Active entries refetch so on-screen template UI is correct immediately.
      // Inactive ones are dropped instead: the call-scribe observer sits
      // disabled on every console page, which holds the list in cache without
      // making it active, so refetching only type: "active" would skip it and
      // the freshness window would hide an update missed during the outage.
      // Marking it stale is not enough — every setQueryData clears that flag —
      // whereas removal cannot be undone, since the cache updaters return
      // undefined for an absent entry and setQueryData no-ops on undefined.
      const reconcileTemplates = () => {
        evictInactiveTemplates();
        void Promise.all([queryClient.refetchQueries({
          queryKey: noteQueryKeys.templates(),
          exact: true,
          type: "active"
        }), queryClient.refetchQueries({
          queryKey: noteQueryKeys.templateDetails(),
          exact: false,
          type: "active"
        }),
        // Which templates changed during the outage is unknown, so every note
        // embedding one refetches.
        queryClient.invalidateQueries({
          queryKey: noteQueryKeys.all,
          predicate: query => (query.state.data as NoteDetail | null | undefined)?.template != null
        })]);
      };
      timers_0.push(setTimeout(reconcileTemplates, 1500));
      timers_0.push(setTimeout(reconcileTemplates, 10_000));
    }
    previousConnectedRef.current = isConnected;
    return () => timers_0.forEach(clearTimeout);
  }, [isConnected]);
}